declare global {
    interface Window {
        grecaptcha?: {
            ready: (callback: () => void) => void;
            execute: (siteKey: string, options: { action: string }) => Promise<string>;
        };
    }
}

/**
 * Action names for the protected endpoints. These must match Filters/BotProtectionActions.cs,
 * which checks the action Google returns, so a token minted for a cheap surface cannot be
 * replayed against a more sensitive one. Adding a surface here means adding it there too.
 */
export const RecaptchaAction = {
    Register: "client_portal_register",
    ResendVerificationCode: "client_portal_resend_code",
    Payment: "client_portal_payment",
    InvoicePayment: "client_portal_invoice_payment",
    SaveCard: "client_portal_save_card",
} as const;

export type RecaptchaActionName = (typeof RecaptchaAction)[keyof typeof RecaptchaAction];

export const RECAPTCHA_TOKEN_HEADER = "X-Recaptcha-Token";

const SCRIPT_TIMEOUT_MS = 10_000;

let scriptPromise: Promise<void> | null = null;

function clearScriptPromise(attempt: Promise<void>) {
    // A timed-out load stays pending, so its late error must not drop the attempt that replaced it.
    if (scriptPromise === attempt) {
        scriptPromise = null;
    }
}

function loadRecaptcha(siteKey: string): Promise<void> {
    if (window.grecaptcha) {
        return Promise.resolve();
    }

    if (scriptPromise !== null) {
        return scriptPromise;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;

    let rejectLoad: (error: Error) => void = () => {};
    const attempt = new Promise<void>((resolve, reject) => {
        rejectLoad = reject;
        script.onload = () => resolve();
    });

    script.onerror = () => {
        clearScriptPromise(attempt);
        rejectLoad(new Error("reCAPTCHA could not be loaded"));
    };

    scriptPromise = attempt;
    document.head.appendChild(script);

    return attempt;
}

/**
 * Fetches the script ahead of the user's click so the token request itself is quick. The token is
 * still minted on the click, because it only lives two minutes.
 */
export function warmRecaptcha(siteKey: string): void {
    void loadRecaptcha(siteKey).catch(() => {
        // Nothing to do here. The click retries the load, and a failure there is refused by the
        // server with a message the surface already knows how to show.
    });
}

function withTimeout<T>(promise: Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("reCAPTCHA timed out")), SCRIPT_TIMEOUT_MS);
        promise.then(resolve, reject).finally(() => clearTimeout(timer));
    });
}

function execute(siteKey: string, action: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const { grecaptcha } = window;

        if (!grecaptcha) {
            reject(new Error("reCAPTCHA is not available"));
            return;
        }

        grecaptcha.ready(() => {
            grecaptcha.execute(siteKey, { action }).then(resolve, reject);
        });
    });
}

/**
 * Returns null rather than throwing when Google cannot be reached or is too slow. The request then
 * goes out without a token and the server refuses it with a message, which keeps the failure on
 * the error path each surface already handles instead of leaving the user on a spinner.
 */
export async function getRecaptchaToken(siteKey: string, action: RecaptchaActionName): Promise<string | null> {
    const load = loadRecaptcha(siteKey);

    try {
        try {
            await withTimeout(load);
        } catch (error) {
            clearScriptPromise(load);
            throw error;
        }

        return await withTimeout(execute(siteKey, action));
    } catch {
        return null;
    }
}
